import ExcelJS from 'exceljs';
import type { LeaseResult, RegionalSummaryItem, RFPConfig, PricingStrategyConfig, LeaseRow } from '../rfpTypes';
import { COMPONENT_LABELS } from '../rfpTypes';
import { marginColor } from '../rfpUtils';

interface ResultsTableProps {
    results: LeaseResult[];
    regionalSummary: RegionalSummaryItem[];
    config: RFPConfig;
    pricing: PricingStrategyConfig;
    month: number;
    year: number;
    leaseRows: LeaseRow[];
    mcEnabled?: boolean;
}

const fmtUsd = (v: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const fmtBbl = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 });

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

async function exportToExcel(
    results: LeaseResult[],
    regional: RegionalSummaryItem[],
    config: RFPConfig,
    pricing: PricingStrategyConfig,
    month: number,
    year: number,
    leaseRows: LeaseRow[],
    mcEnabled: boolean,
) {
    const wb = new ExcelJS.Workbook();
    const hasRegion = results.some((r) => r.region);

    // ── Inputs sheet ──────────────────────────────────────────────────────────
    const wsIn = wb.addWorksheet('Inputs');
    wsIn.columns = [
        { key: 'label', width: 28 },
        { key: 'value', width: 40 },
    ];

    const hdrFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
    const hdrFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    const secFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
    const secFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
    const labelFont: Partial<ExcelJS.Font> = { color: { argb: 'FF374151' } };

    const addHeader = (title: string) => {
        const r = wsIn.addRow([title]);
        r.getCell(1).fill  = hdrFill;
        r.getCell(1).font  = hdrFont;
        r.getCell(2).fill  = hdrFill;
        wsIn.mergeCells(`A${r.number}:B${r.number}`);
    };
    const addSection = (title: string) => {
        wsIn.addRow([]);
        const r = wsIn.addRow([title]);
        r.getCell(1).fill = secFill;
        r.getCell(1).font = secFont;
        r.getCell(2).fill = secFill;
        wsIn.mergeCells(`A${r.number}:B${r.number}`);
    };
    const addRow = (label: string, value: string | number) => {
        const r = wsIn.addRow([label, value]);
        r.getCell(1).font = labelFont;
    };
    const addBswSchedule = (schedule: RFPConfig['primary_destination']['bsw_schedule']) => {
        if (!schedule || schedule.length === 0) {
            addRow('  BS&W Schedule', 'None');
        } else {
            schedule.forEach((b) => {
                const typeLabel = (b.deduct_type ?? 'fixed') === 'percent' ? '% of price' : '$/bbl';
                addRow(`  ${b.bsw_low}% – ${b.bsw_high}%`, `${b.discount} ${typeLabel}`);
            });
        }
    };
    const destBlock = (label: string, dest: RFPConfig['primary_destination'] | undefined) => {
        if (!dest || !dest.station) { addRow(label, 'Not configured'); return; }
        addRow(label, `${dest.station} — ${dest.market} (${dest.station_owner})`);
        addRow('  Base Deduct', `${dest.base_deduct} $/bbl`);
        addBswSchedule(dest.bsw_schedule);
        // Sale-side components
        addRow('  WTI/WTL Split', dest.wti_wtl_enabled ? `Enabled (≥ ${dest.wti_wtl_gravity_threshold}° API → WTL)` : 'Disabled');
        const activeDestComps = Object.entries(dest.component_flags ?? {})
            .filter(([, on]) => on)
            .map(([k]) => COMPONENT_LABELS[k as keyof typeof COMPONENT_LABELS] ?? k)
            .join(', ');
        addRow('  Sale Components', activeDestComps || 'None');
    };

    addHeader(`RFP Analysis — ${MONTH_NAMES[month - 1]} ${year}`);

    // General
    addSection('General');
    addRow('Analysis Month', `${MONTH_NAMES[month - 1]} ${year}`);
    addRow('Carrier', config.carrier || '—');
    addRow('BS&W Threshold (%)', config.bsw_threshold);

    // Destinations
    addSection('Destinations');
    destBlock('Primary Destination', config.primary_destination);
    destBlock('Secondary Destination', config.secondary_destination);
    destBlock('Waste Destination', config.waste_destination);

    // Pricing Strategy
    addSection('Pricing Strategy');
    addRow('Base Deduct ($/bbl)', pricing.base_deduct);
    addRow('WTI/WTL Gravity Split', pricing.wti_wtl_enabled ? `Enabled (≥ ${pricing.wti_wtl_gravity_threshold}° API → WTL)` : 'Disabled');
    addSection('  Purchase BS&W Schedule');
    addBswSchedule(pricing.bsw_schedule);

    // Components
    addSection('Components');
    const activeComponents = Object.entries(pricing.component_flags)
        .filter(([, on]) => on)
        .map(([k]) => `${COMPONENT_LABELS[k as keyof typeof COMPONENT_LABELS]} = ${pricing.component_values[k] ?? 0}`);
    if (activeComponents.length === 0) {
        addRow('Active Components', 'None');
    } else {
        activeComponents.forEach((line) => {
            const [name, val] = line.split(' = ');
            addRow(name, val);
        });
    }

    // --- Per Lease sheet ---
    const ws1 = wb.addWorksheet('Per Lease');
    const perLeaseHeaders = [
        { header: 'Lease Name', key: 'lease_name', width: 22 },
        ...(hasRegion ? [{ header: 'Region', key: 'region', width: 14 }] : []),
        { header: 'Avg BBL/mo', key: 'avg_monthly_bbl', width: 14 },
        { header: 'Avg BS&W %', key: 'avg_bsw', width: 12 },
        { header: 'Avg Gravity', key: 'avg_gravity', width: 13 },
        { header: 'Destination', key: 'destination_type', width: 13 },
        { header: 'Station', key: 'destination_station', width: 22 },
        { header: 'Market', key: 'destination_market', width: 18 },
        { header: 'Mileage', key: 'mileage', width: 10 },
        { header: 'Trucking Rate', key: 'trucking_rate', width: 14 },
        { header: 'Purchase Price', key: 'purchase_price', width: 15 },
        { header: 'Purchase Deduct', key: 'purchase_price_deduct', width: 16 },
        { header: 'Sale Price', key: 'sale_price', width: 12 },
        { header: 'Sale Deduct', key: 'sale_price_deduct', width: 13 },
        { header: 'Margin', key: 'margin', width: 10 },
        ...(mcEnabled ? [
            { header: 'MC P10', key: 'mc_p10', width: 10 },
            { header: 'MC P25', key: 'mc_p25', width: 10 },
            { header: 'MC P50', key: 'mc_p50', width: 10 },
            { header: 'MC P75', key: 'mc_p75', width: 10 },
            { header: 'MC P90', key: 'mc_p90', width: 10 },
            { header: 'MC Std Dev', key: 'mc_std', width: 12 },
            { header: 'MC Loads', key: 'mc_loads', width: 10 },
        ] : []),
    ];
    ws1.columns = perLeaseHeaders;
    // Header style
    ws1.getRow(1).font = { bold: true };
    ws1.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };

    results.forEach((r) => {
        const row = ws1.addRow({
            lease_name: r.lease_name,
            region: r.region ?? '',
            avg_monthly_bbl: r.avg_monthly_bbl,
            avg_bsw: r.avg_bsw,
            avg_gravity: r.avg_gravity,
            destination_type: r.destination_type,
            destination_station: r.destination_station,
            destination_market: r.destination_market,
            mileage: r.mileage,
            trucking_rate: r.trucking_rate,
            purchase_price: r.purchase_price,
            purchase_price_deduct: r.purchase_price_deduct,
            sale_price: r.sale_price,
            sale_price_deduct: r.sale_price_deduct,
            margin: r.margin,
            ...(mcEnabled ? { mc_p10: r.mc_p10, mc_p25: r.mc_p25, mc_p50: r.mc_p50, mc_p75: r.mc_p75, mc_p90: r.mc_p90, mc_std: r.mc_std, mc_loads: r.mc_loads } : {}),
        });
        // Color margin cell
        const marginCell = row.getCell('margin');
        const argb = r.margin >= 2 ? 'FF86EFAC' : r.margin >= 0 ? 'FFFDE68A' : 'FFFECACA';
        marginCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
        // Color MC P10/P50/P90 cells
        if (mcEnabled && r.mc_p10 != null) {
            const mcArgb = (v: number) => v >= 2 ? 'FF86EFAC' : v >= 0 ? 'FFFDE68A' : 'FFFECACA';
            (['mc_p10','mc_p25','mc_p50','mc_p75','mc_p90'] as const).forEach(k => {
                const cell = row.getCell(k);
                const val = r[k] as number;
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: mcArgb(val) } };
            });
        }
    });

    // --- Regional Summary sheet ---
    if (regional.length) {
        const ws2 = wb.addWorksheet('Regional Summary');
        ws2.columns = [
            { header: 'Region', key: 'region', width: 18 },
            { header: 'Total BBL/mo', key: 'total_monthly_bbl', width: 14 },
            { header: 'Avg Purchase Price', key: 'avg_purchase_price', width: 18 },
            { header: 'Avg Sale Price', key: 'avg_sale_price', width: 16 },
            { header: 'Avg Trucking Rate', key: 'avg_trucking_rate', width: 18 },
            { header: 'Avg Margin', key: 'avg_margin', width: 12 },
        ];
        ws2.getRow(1).font = { bold: true };
        ws2.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
        regional.forEach((r) => ws2.addRow(r));
    }

    // --- Hidden state sheet for re-upload ---
    const wsState = wb.addWorksheet('_rfp_state');
    wsState.state = 'veryHidden';
    wsState.getCell('A1').value = JSON.stringify({ config, pricing, month, year, leaseRows });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RFP_Analysis_${year}_${String(month).padStart(2, '0')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
}

export default function ResultsTable({ results, regionalSummary, config, pricing, month, year, leaseRows, mcEnabled = false }: ResultsTableProps) {
    const hasRegion = results.some((r) => r.region);

    // ── Total summary ─────────────────────────────────────────────────────────
    const totalBbl   = results.reduce((s, r) => s + r.avg_monthly_bbl, 0);
    const wavg = (field: keyof LeaseResult) =>
        totalBbl > 0
            ? results.reduce((s, r) => s + (r[field] as number) * r.avg_monthly_bbl, 0) / totalBbl
            : 0;
    const avgPurchase  = wavg('purchase_price');
    const avgSale      = wavg('sale_price');
    const avgTrucking  = wavg('trucking_rate');
    const avgMargin    = wavg('margin');
    const monthRevenue = totalBbl * avgSale;
    const monthCost    = totalBbl * (avgPurchase + avgTrucking);
    const monthProfit  = totalBbl * avgMargin;

    const fmtDollar = (v: number) =>
        v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                    Results — {results.length} lease{results.length !== 1 ? 's' : ''}
                </h4>
                <button
                    onClick={() => exportToExcel(results, regionalSummary, config, pricing, month, year, leaseRows, mcEnabled)}
                    style={{
                        padding: '4px 14px',
                        background: '#16a34a',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 5,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    Export Excel
                </button>
            </div>

            {/* ── Total summary strip ── */}
            {results.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 12 }}>
                    <SummaryCard label="Total BBL/mo"          value={fmtBbl(totalBbl)}                    tooltip="Sum of avg monthly BBL across all leases" />
                    <SummaryCard label="Wtd Avg Purchase"      value={fmtUsd(avgPurchase)}                tooltip="Production-weighted average purchase price ($/bbl), weighted by avg monthly BBL per lease" />
                    <SummaryCard label="Wtd Avg Sale"          value={fmtUsd(avgSale)}                    tooltip="Production-weighted average sale price ($/bbl), weighted by avg monthly BBL per lease" />
                    <SummaryCard label="Wtd Avg Trucking"      value={fmtUsd(avgTrucking)}                tooltip="Production-weighted average trucking rate ($/bbl), weighted by avg monthly BBL per lease" />
                    <SummaryCard label="Wtd Avg Margin"        value={fmtUsd(avgMargin)}  color={marginColor(avgMargin)} tooltip="Production-weighted average margin ($/bbl) = Wtd Avg Sale − Wtd Avg Purchase − Wtd Avg Trucking, weighted by avg monthly BBL per lease" />
                    <div style={{ width: 1, background: '#bae6fd', margin: '0 4px', alignSelf: 'stretch' }} />
                    <SummaryCard label="Est. Monthly Revenue"  value={fmtDollar(monthRevenue)}            tooltip={`Total BBL/mo × Wtd Avg Sale Price = ${fmtBbl(totalBbl)} × ${fmtUsd(avgSale)}`} />
                    <SummaryCard label="Est. Monthly Cost"     value={fmtDollar(monthCost)}               tooltip={`Total BBL/mo × (Wtd Avg Purchase + Wtd Avg Trucking) = ${fmtBbl(totalBbl)} × (${fmtUsd(avgPurchase)} + ${fmtUsd(avgTrucking)})`} />
                    <SummaryCard label="Est. Monthly Profit"   value={fmtDollar(monthProfit)} color={marginColor(avgMargin)} tooltip={`Total BBL/mo × Wtd Avg Margin = ${fmtBbl(totalBbl)} × ${fmtUsd(avgMargin)}`} />
                </div>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: '#dbeafe', position: 'sticky', top: 0 }}>
                            <Th>Lease Name</Th>
                            {hasRegion && <Th>Region</Th>}
                            <Th right>Avg BBL/mo</Th>
                            <Th right>Avg BS&W %</Th>
                            <Th right>Avg Gravity</Th>
                            <Th>Dest</Th>
                            <Th>Station</Th>
                            <Th right>Miles</Th>
                            <Th right>Trucking $/bbl</Th>
                            <Th right>Purch Price</Th>
                            <Th right>Purch Deduct</Th>
                            <Th right>Sale Price</Th>
                            <Th right>Sale Deduct</Th>
                            <Th right>Margin</Th>
                            {mcEnabled && <>
                                <Th right>MC P10</Th>
                                <Th right>MC P50</Th>
                                <Th right>MC P90</Th>
                                <Th right>MC Std</Th>
                            </>}
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((r, i) => (
                            <tr
                                key={i}
                                style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}
                            >
                                <Td>{r.lease_name}</Td>
                                {hasRegion && <Td>{r.region ?? '—'}</Td>}
                                <Td right>{fmtBbl(r.avg_monthly_bbl)}</Td>
                                <Td right>{r.avg_bsw.toFixed(2)}</Td>
                                <Td right>{r.avg_gravity.toFixed(1)}</Td>
                                <Td>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            padding: '1px 5px',
                                            borderRadius: 3,
                                            background:
                                                r.destination_type === 'Primary'
                                                    ? '#d1fae5'
                                                    : r.destination_type === 'Secondary'
                                                    ? '#fef3c7'
                                                    : '#fee2e2',
                                            color: '#374151',
                                        }}
                                    >
                                        {r.destination_type}
                                    </span>
                                </Td>
                                <Td>{r.destination_station}</Td>
                                <Td right>{r.mileage}</Td>
                                <Td right>{fmtUsd(r.trucking_rate)}</Td>
                                <Td right>{fmtUsd(r.purchase_price)}</Td>
                                <Td right>{fmtUsd(r.purchase_price_deduct)}</Td>
                                <Td right>{fmtUsd(r.sale_price)}</Td>
                                <Td right>{fmtUsd(r.sale_price_deduct)}</Td>
                                <td
                                    style={{
                                        padding: '3px 8px',
                                        textAlign: 'right',
                                        fontWeight: 700,
                                        color: marginColor(r.margin),
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {fmtUsd(r.margin)}
                                </td>
                                {mcEnabled && r.mc_p10 != null && <>
                                    <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, color: marginColor(r.mc_p10!), whiteSpace: 'nowrap', fontSize: 11 }}>{fmtUsd(r.mc_p10!)}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, color: marginColor(r.mc_p50!), whiteSpace: 'nowrap', fontSize: 11 }}>{fmtUsd(r.mc_p50!)}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, color: marginColor(r.mc_p90!), whiteSpace: 'nowrap', fontSize: 11 }}>{fmtUsd(r.mc_p90!)}</td>
                                    <td style={{ padding: '3px 8px', textAlign: 'right', color: '#6b7280', whiteSpace: 'nowrap', fontSize: 11 }}>±{fmtUsd(r.mc_std!)}</td>
                                </>}
                                {mcEnabled && r.mc_p10 == null && <td colSpan={4} />}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
    return (
        <th
            style={{
                padding: '5px 8px',
                textAlign: right ? 'right' : 'left',
                fontWeight: 700,
                color: '#1e3a5f',
                whiteSpace: 'nowrap',
                borderBottom: '2px solid #93c5fd',
            }}
        >
            {children}
        </th>
    );
}

function Td({ children, right }: { children: React.ReactNode; right?: boolean }) {
    return (
        <td style={{ padding: '3px 8px', textAlign: right ? 'right' : 'left', color: '#374151', whiteSpace: 'nowrap' }}>
            {children}
        </td>
    );
}

function SummaryCard({ label, value, color, tooltip }: { label: string; value: string; color?: string; tooltip?: string }) {
    return (
        <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 110, cursor: tooltip ? 'help' : 'default' }}>
            <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                {label}{tooltip && <span style={{ marginLeft: 3, fontSize: 9, color: '#93c5fd' }}>ⓘ</span>}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: color ?? '#1e3a5f' }}>{value}</span>
        </div>
    );
}
